import React from 'react';
import { KatButton, KatSpinner } from '@amzn/katal-react';
import { FunctionMetadata, TERMINAL_JOB_STATUSES } from '../../../services/Batcher/BatcherService';

interface SubmitTabProps {
    chain: FunctionMetadata[];
    rowCount: number;
    submitting: boolean;
    submitError: string;
    jobId: string;
    jobStatus: string;
    jobError: string;
    /** Scheme-validated (https-only) download URL, or '' if none/unsafe. */
    safeDownloadUrl: string;
    /** Row count captured at submit time — the progress bar denominator. */
    submittedRowCount: number;
    onSubmit: () => void;
}

/** Step 3: review, submit the job, and track its status/result. */
const SubmitTab: React.FC<SubmitTabProps> = ({
    chain,
    rowCount,
    submitting,
    submitError,
    jobId,
    jobStatus,
    jobError,
    safeDownloadUrl,
    submittedRowCount,
    onSubmit,
}) => (
    <div className="batcher-panel">
        <div className="batcher-submit-body">
            <div className="batcher-submit-summary">
                <div className="batcher-submit-summary-row">
                    <span className="batcher-submit-summary-label">Functions</span>
                    <span className="batcher-submit-summary-value">
                        {chain.map(fn => fn.functionName).join(' → ')}
                    </span>
                </div>
                <div className="batcher-submit-summary-row">
                    <span className="batcher-submit-summary-label">Input rows</span>
                    <span className="batcher-submit-summary-value">{rowCount}</span>
                </div>
            </div>

            {submitError && <div className="batcher-error">{submitError}</div>}

            {jobId ? (
                <div className="batcher-job-result">
                    <div className="batcher-submit-success">
                        Job submitted. Job ID: <span className="batcher-job-id">{jobId}</span>
                    </div>

                    <div className="batcher-job-status-row">
                        {!TERMINAL_JOB_STATUSES.includes(jobStatus) && !jobError && <KatSpinner size="small" />}
                        <span className="batcher-job-status">Status: {jobStatus || 'PENDING'}</span>
                    </div>

                    {!jobError && (
                        <div className="batcher-progress">
                            <div className="batcher-progress-track">
                                <div
                                    className="batcher-progress-fill"
                                    style={{ width: `${jobStatus === 'SUCCEEDED' ? 100 : 0}%` }}
                                />
                            </div>
                            <span className="batcher-progress-text">
                                {jobStatus === 'SUCCEEDED' ? submittedRowCount : 0}/{submittedRowCount} rows processed
                            </span>
                        </div>
                    )}

                    {jobError && <div className="batcher-error">{jobError}</div>}

                    {jobStatus === 'SUCCEEDED' && safeDownloadUrl && (
                        <a
                            className="batcher-download-button"
                            href={safeDownloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                        >
                            Download result CSV
                        </a>
                    )}
                </div>
            ) : (
                <KatButton
                    className="batcher-submit-button"
                    variant="primary"
                    onClick={onSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'Submitting...' : 'Submit job'}
                </KatButton>
            )}
        </div>
    </div>
);

export default SubmitTab;
